import { useState } from 'react';

import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { type FieldMapping, autoMapFields } from '../../utils/wizardHelpers';

interface Field {
  name: string;
  label: string;
  type: string;
}

interface WizardMappingStepProps {
  sourceObject: string;
  destinationObject: string;
  sourceFields: Field[];
  destinationFields: Field[];
  showApiNames: boolean;
  onNext: (mappings: FieldMapping[]) => void;
  onSaveAndExit?: () => void;
  initialMappings?: FieldMapping[];
}

export const WizardMappingStep = ({
  sourceObject,
  destinationObject,
  sourceFields,
  destinationFields,
  showApiNames,
  onNext,
  onSaveAndExit,
  initialMappings = [],
}: WizardMappingStepProps) => {
  const [mappings, setMappings] = useState<FieldMapping[]>(initialMappings);

  const handleAddMapping = () => {
    setMappings([...mappings, { sourceField: '', destinationField: '' }]);
  };

  const handleRemoveMapping = (index: number) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  const handleUpdateMapping = (index: number, field: keyof FieldMapping, value: string) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: value };
    setMappings(updated);
  };

  const handleAutoMap = () => {
    // Use auto-mapping helper with enhanced logic
    const autoMappings = autoMapFields(sourceFields, destinationFields, {
      caseSensitive: false,
      matchByType: true,
      includePartialMatches: true,
    });

    setMappings(autoMappings);
  };

  const handleNext = () => {
    const validMappings = mappings.filter((m) => m.sourceField && m.destinationField);
    if (validMappings.length > 0) {
      onNext(validMappings);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Map Fields
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Map fields from {sourceObject} to {destinationObject}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button variant="outlined" size="small" onClick={handleAutoMap}>
          Auto-Map by Name
        </Button>
        <Button variant="outlined" size="small" onClick={handleAddMapping}>
          Add Mapping
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source Field</TableCell>
              <TableCell>Destination Field</TableCell>
              <TableCell>Transform</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((mapping, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={index}>
                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={mapping.sourceField}
                      onChange={(e) => handleUpdateMapping(index, 'sourceField', e.target.value)}
                    >
                      {sourceFields.map((field) => (
                        <MenuItem key={field.name} value={field.name}>
                          <Tooltip title={`Type: ${field.type}`} placement="left">
                            <span>{showApiNames ? field.name : field.label}</span>
                          </Tooltip>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={mapping.destinationField}
                      onChange={(e) =>
                        handleUpdateMapping(index, 'destinationField', e.target.value)
                      }
                    >
                      {destinationFields.map((field) => (
                        <MenuItem key={field.name} value={field.name}>
                          <Tooltip title={`Type: ${field.type}`} placement="right">
                            <span>{showApiNames ? field.name : field.label}</span>
                          </Tooltip>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Optional"
                    value={mapping.transform || ''}
                    onChange={(e) => handleUpdateMapping(index, 'transform', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  {mapping.confidence && (
                    <Tooltip title={`Confidence: ${mapping.confidence}`}>
                      <Typography variant="caption" sx={{ mr: 1 }}>
                        {mapping.confidence === 'high'
                          ? '✓'
                          : mapping.confidence === 'medium'
                            ? '~'
                            : '?'}
                      </Typography>
                    </Tooltip>
                  )}
                  <Button size="small" color="error" onClick={() => handleRemoveMapping(index)}>
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
        <Button variant="contained" onClick={handleNext} disabled={mappings.length === 0}>
          Next
        </Button>
        {onSaveAndExit && (
          <Button variant="outlined" onClick={onSaveAndExit}>
            Save and Exit
          </Button>
        )}
      </Box>
    </Box>
  );
};
